'use client';

import { company, addressLine } from '@/lib/company';

/**
 * The verifiable corporate record, rendered as a definition list so it reads
 * as data rather than marketing. This is the block a funder, a procurement
 * reviewer or a bank looks for — so it is plain, complete and copyable.
 *
 * The Ontario corporation number lives here and in the footer, and nowhere in
 * structured data: schema.org has no property that carries it without
 * asserting something false about what it is.
 */
export function CorporateDetailsSection({
  heading = 'Corporate details',
  eyebrow = 'Verifiable record',
  id = 'corporate-details',
}: {
  heading?: string;
  eyebrow?: string;
  id?: string;
}) {
  const rows: Array<{ label: string; value: React.ReactNode }> = [
    { label: 'Legal name', value: company.legalName },
    { label: 'Entity type', value: 'Corporation' },
    { label: 'Jurisdiction of incorporation', value: company.incorporatedIn },
    { label: 'Ontario corporation number', value: company.corporationNumber },
    {
      label: 'Registered address',
      value: (
        <address className="not-italic">{addressLine}</address>
      ),
    },
    { label: 'Primary contact', value: `${company.contactName} — ${company.contactRole}` },
    {
      label: 'Email',
      value: (
        <a href={`mailto:${company.email}`} className="underline decoration-white/25 underline-offset-4 transition-colors hover:decoration-white">
          {company.email}
        </a>
      ),
    },
    {
      label: 'Telephone',
      value: (
        <a href={`tel:${company.telHref}`} className="underline decoration-white/25 underline-offset-4 transition-colors hover:decoration-white">
          {company.phone}
        </a>
      ),
    },
    { label: 'Governing law', value: company.governingLaw },
    {
      label: 'Website',
      value: (
        <a href={company.url} className="underline decoration-white/25 underline-offset-4 transition-colors hover:decoration-white">
          {company.domain}
        </a>
      ),
    },
  ];

  return (
    <section
      id={id}
      className="self-styled relative z-20 border-b border-white/[0.07]"
      style={{ backgroundColor: 'rgba(10,10,10,0.72)' }}
      aria-labelledby={`${id}-heading`}
    >
      <div className="content-padding">
        <div className="grid grid-cols-1 gap-10 py-24 md:py-32 lg:grid-cols-[minmax(0,20rem)_1fr] lg:gap-20">
          <div>
            <span className="eyebrow">{eyebrow}</span>
            <h2
              id={`${id}-heading`}
              className="font-six-caps mt-6 uppercase leading-[0.88] text-white"
              style={{ fontSize: 'clamp(2.25rem, 5vw, 4rem)' }}
            >
              {heading}
            </h2>
            <p className="mt-6 max-w-xs text-sm font-light leading-relaxed text-white/50">
              Everything on this page can be checked against the Ontario corporate
              record or by contacting us directly.
            </p>
          </div>

          <dl className="flex flex-col">
            {rows.map((row) => (
              <div
                key={row.label}
                className="grid grid-cols-1 gap-1 border-t border-white/10 py-5 sm:grid-cols-[minmax(0,16rem)_1fr] sm:items-baseline sm:gap-8 sm:py-6"
              >
                <dt className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/40">
                  {row.label}
                </dt>
                <dd className="text-[15px] font-light leading-relaxed text-white/85 md:text-base">
                  {row.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
}
